import { TeamOutlined, UserOutlined } from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { Avatar, Button, Divider, Input, List, Modal, Typography } from 'antd'
import { getOperationName } from 'apollo-link'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DepartmentsDisplay, UserHoverCard, UserListModal } from '@/components'
import {
  GetGroupAdminsDocument,
  GetGroupDocument,
  GetGroupFollowersDocument,
  GetUserListDocument,
  Group,
  GroupPrivateSetting,
  useGetGroupAdminsQuery,
  useGetGroupFollowersQuery,
  useGroupAdminAddMutation,
  useGroupAdminRemoveMutation,
  useGroupFollowerRemoveMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'

import s from './group-sidebar.module.less'

interface GroupUsersProps {
  group: Group
  isGroupAdmin?: boolean
  query: typeof useGetGroupFollowersQuery | typeof useGetGroupAdminsQuery
  title: string
  isManagedMode?: boolean
}
interface ManageGroupAdminProps {
  group: Group
  isGroupAdmin: boolean
}

export const GroupUserModal: React.FC<Omit<GroupUsersProps, 'title'>> = ({ isManagedMode, group, query }) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const last = 6
  const { data, fetchMore, loading } = query({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    variables: {
      groupId: group.groupId,
      usersFilter: {
        search,
      },
      pagination: {
        last,
      },
    },
  })
  const refetchQueries = [getOperationName(GetGroupAdminsDocument)!, getOperationName(GetGroupDocument)!]
  const [addGroupAdmin] = useGroupAdminAddMutation({
    refetchQueries,
  })
  const [removeGroupAdmin] = useGroupAdminRemoveMutation({
    refetchQueries,
  })
  const users = (data ? data.group.users.edges.slice().reverse() : []).map(({ node }) => node)
  const pageInfo = data
    ? data.group.users.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: null,
        hasNextPage: null,
      }

  const loadMore = pageInfo.hasPreviousPage && (
    <Button
      style={{ marginTop: 8 }}
      onClick={() => fetchMore({ variables: { pagination: { last, before: pageInfo.startCursor } } })}
    >
      {t('groups.grouplist.loadmore')}
    </Button>
  )
  const Action = ({ userId }: { userId: number }) => {
    if (!isManagedMode) {
      return null
    }
    const text = query == useGetGroupAdminsQuery ? t('admin.action.remove') : t('admin.action.add')
    const onClick = () => {
      ;(query == useGetGroupAdminsQuery ? removeGroupAdmin : addGroupAdmin)({
        variables: {
          groupId: group.groupId,
          userId,
        },
      })
    }
    return (
      <Button disabled={userId === group.creatorUserId} onClick={onClick}>
        {text}
      </Button>
    )
  }
  const doSearch = query == useGetGroupFollowersQuery && isManagedMode
  const groupAdminsId = group.admins.edges.map(({ node }) => node.userId)
  const adminCandidates = users.filter((u) => !groupAdminsId.includes(u.userId))

  return (
    <div>
      {doSearch && <Input.Search onSearch={(s) => setSearch(s)} />}
      <List
        dataSource={doSearch ? adminCandidates : users}
        renderItem={(u) => (
          <List.Item actions={[<Action key='action' userId={u.userId} />]}>
            <List.Item.Meta
              avatar={<Avatar src={u.avatarUrl} style={{ marginRight: 12, marginLeft: 20, marginTop: 4 }} />}
              title={<Link to={routes.users.page(u.email)}>{u.name}</Link>}
              description={u.email}
            />
          </List.Item>
        )}
        loadMore={loadMore}
        loading={loading}
      />
    </div>
  )
}

export const ManageGroupAdmin: React.FC<ManageGroupAdminProps> = ({ group, isGroupAdmin }) => {
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()

  return (
    <>
      <Modal visible={visible} footer={false} onCancel={() => toggle()} destroyOnClose={true}>
        <div>
          <Typography.Title
            style={{ display: 'flex', justifyContent: 'center', fontSize: 20, fontWeight: 600 }}
            level={5}
          >
            {t('admin.group.admin')}
          </Typography.Title>
          <GroupUserModal group={group} isManagedMode={isGroupAdmin} query={useGetGroupAdminsQuery}></GroupUserModal>
        </div>
        <Divider />
        <Typography.Title
          style={{ display: 'flex', justifyContent: 'center', fontSize: 20, fontWeight: 600 }}
          level={5}
        >
          {t('admin.group.adminCandidate')}
        </Typography.Title>
        <div>
          <GroupUserModal group={group} isManagedMode={isGroupAdmin} query={useGetGroupFollowersQuery}></GroupUserModal>
        </div>
      </Modal>
      <Button className={s.manage} type='link' onClick={() => toggle()}>
        {t('admin.group.manage')}
      </Button>
    </>
  )
}

export const ManageGroupMember: React.FC<{ group: Group }> = ({ group }) => {
  const { t } = useTranslation()

  const [groupRemoveFollow] = useGroupFollowerRemoveMutation({
    refetchQueries: [
      getOperationName(GetUserListDocument)!,
      getOperationName(GetGroupDocument)!,
      getOperationName(GetGroupFollowersDocument)!,
    ],
  })

  const extra = (userId: number) => (
    <Button
      danger
      type='ghost'
      disabled={userId === group.creatorUserId}
      onClick={() => groupRemoveFollow({ variables: { followeeGroupId: group.groupId, followerUserId: userId } })}
    >
      {t('admin.action.removeMember')}
    </Button>
  )

  return (
    <UserListModal
      title={t('admin.group.manageMember')}
      groupId={group.groupId}
      type='groupMembers'
      extra={(userId: number) => extra(userId)}
      onClickUser={() => {}}
    >
      <Button className={s.manage} type='link'>
        {t('admin.group.manage')}
      </Button>
    </UserListModal>
  )
}

export const GroupSidebarUsers: React.FC<GroupUsersProps> = ({ group, isGroupAdmin, query, title }) => {
  const { data, error } = query({
    fetchPolicy: 'no-cache',
    variables: {
      groupId: group.groupId,
      pagination: {
        last: 10,
      },
      usersFilter: {},
    },
  })

  if (error || !data) {
    return null
  }

  const users = (data ? data.group.users.edges : [])
    .slice()
    .reverse()
    .map(({ node }) => node)

  const totalCount = data ? data.group.users.totalCount : 0
  const isFollowerList = query == useGetGroupFollowersQuery

  const titleComponent = (
    <UserListModal groupId={group.groupId} type={isFollowerList ? 'groupMembers' : 'groupAdmins'} title={title}>
      <div className={s.followersHeader}>
        {isFollowerList ? <TeamOutlined /> : <UserOutlined />}
        {title + ` · ${totalCount}`}
      </div>
    </UserListModal>
  )

  const header = (
    <div className={s.header}>
      <Typography.Title level={5}>{titleComponent}</Typography.Title>
      {isGroupAdmin && !isFollowerList && <ManageGroupAdmin group={group} isGroupAdmin={isGroupAdmin} />}
      {isGroupAdmin && isFollowerList && group.privateSetting !== GroupPrivateSetting.Normal && (
        <ManageGroupMember group={group} />
      )}
    </div>
  )

  return (
    <List
      header={header}
      itemLayout='horizontal'
      size='small'
      split={false}
      dataSource={users}
      renderItem={(u) => (
        <List.Item key={u.userId}>
          <List.Item.Meta
            avatar={<Avatar src={u.avatarUrl} style={{ marginLeft: 20 }} />}
            title={
              <UserHoverCard email={u.email}>
                <Link to={routes.users.page(u.email)}>{u.name}</Link>
              </UserHoverCard>
            }
            description={<DepartmentsDisplay departments={u.departments} />}
          />
        </List.Item>
      )}
    />
  )
}
